import {
  SECONDS_IN_A_DAY,
  SECONDS_IN_A_MINUTE,
  SECONDS_IN_AN_HOUR,
} from "@redotech/util/time";
import { assertNever } from "@redotech/util/type";
import {
  FullDateTimeframeCondition,
  TimeframeConditionType,
} from "../marketing/segments/segment-timeframe";
import {
  ActivityCount,
  ActivityCountType,
  CustomerActivityCondition,
  CustomerActivityType,
  CustomerAttributeCondition,
  CustomerCharacteristicType,
  SegmentConditionBlock,
  SegmentConditionType,
} from "../marketing/segments/segment-types";
import { ConjunctionMode } from "../segments/segment-types";
import { BaseStep, StepType, SupportedWaitForEventType } from "./common";
import { SchemaBooleanExpression } from "./expressions/index";
import {
  AddTagActionStep,
  AssignFulfillmentServiceActionStep,
  GeneratePersonalizedSlipActionStep,
  PlaceHoldActionStep,
  PlaceHoldUntilActionStep,
  RoundWeightActionStep,
  SetAssignedUserActionStep,
  SetBuyReturnLabelActionStep,
  SetDeliveryMethodActionStep,
  SetDutiesPayerActionStep,
  SetInternalNoteActionStep,
  SetOriginLocationActionStep,
  SetOverrideUseOriginalDestinationAddressActionStep,
  SetPackingSlipTemplateActionStep,
  SetParcelActionStep,
  SetReturnAddressActionStep,
  SetShippingOptionsActionStep,
  SetShippingServiceActionStep,
  SetShippingServiceCheapestActionStep,
  SetShippingServiceCheapestWithinDeliveryDaysActionStep,
  SetShippingServiceFastestActionStep,
  SetTaxIdentifiersActionStep,
  SetThirdPartyBillingAccountActionStep,
  SetWeightActionStep,
  SplitIntoParcelsActionStep,
} from "./oms/actions";
import {
  OmsAddressVerifiedConditionStep,
  OmsAvailableInventoryConditionStep,
  OmsCustomAttributeDate1ConditionStep,
  OmsCustomAttributeDate2ConditionStep,
  OmsCustomAttributeDate3ConditionStep,
  OmsCustomAttributeNumber1ConditionStep,
  OmsCustomAttributeNumber2ConditionStep,
  OmsCustomAttributeNumber3ConditionStep,
  OmsCustomAttributeString1ConditionStep,
  OmsCustomAttributeString2ConditionStep,
  OmsCustomAttributeString3ConditionStep,
  OmsCustomDate1ConditionStep,
  OmsCustomDate2ConditionStep,
  OmsCustomDate3ConditionStep,
  OmsCustomerNameConditionStep,
  OmsCustomerOrderCountConditionStep,
  OmsCustomerTagConditionStep,
  OmsCustomNumber1ConditionStep,
  OmsCustomNumber2ConditionStep,
  OmsCustomNumber3ConditionStep,
  OmsCustomString1ConditionStep,
  OmsCustomString2ConditionStep,
  OmsCustomString3ConditionStep,
  OmsDeliveryMethodConditionStep,
  OmsDestinationCountryConditionStep,
  OmsDestinationProvinceConditionStep,
  OmsFraudRiskConditionStep,
  OmsFulfillmentLocationConditionStep,
  OmsItemAmountConditionStep,
  OmsItemQuantityConditionStep,
  OmsLineItemCustomAttributeConditionStep,
  OmsMarketplaceConditionStep,
  OmsOrderNotesConditionStep,
  OmsOrderTagConditionStep,
  OmsOriginStoreConditionStep,
  OmsPackageWeightOzConditionStep,
  OmsProductNameConditionStep,
  OmsProductTagConditionStep,
  OmsProductTypeConditionStep,
  OmsSalesChannelConditionStep,
  OmsSelectedShippingConditionStep,
  OmsShipmentTagConditionStep,
  OmsSkuConditionStep,
  OmsStreetAddressConditionStep,
  OmsStreetAddressListInputConditionStep,
  OmsTaxesPaidConditionStep,
  OmsTotalValueConditionStep,
  OmsUspsZoneConditionStep,
  OmsVariantTitleConditionStep,
  OmsVendorConditionStep,
} from "./oms/conditions";
import { SchemaType } from "./schemas/schemas";
import { Category, TriggerKey } from "./triggers";

/** @deprecated Use the new zod flow step types instead */
interface TriggerStep extends BaseStep {
  type: StepType.TRIGGER;
  schemaType: SchemaType;
  category: Category;
  key: TriggerKey;
  nextId: string;
}

export function getAppropriateTimeUnit(seconds: number): WaitTimeUnit {
  if (seconds % SECONDS_IN_A_DAY === 0) {
    return WaitTimeUnit.DAYS;
  }
  if (seconds % SECONDS_IN_AN_HOUR === 0) {
    return WaitTimeUnit.HOURS;
  }
  return WaitTimeUnit.MINUTES;
}

export function getSeconds({
  timeValue,
  timeUnit,
}: {
  timeValue: number;
  timeUnit: WaitTimeUnit;
}): number {
  switch (timeUnit) {
    case WaitTimeUnit.DAYS:
      return timeValue * SECONDS_IN_A_DAY;
    case WaitTimeUnit.HOURS:
      return timeValue * SECONDS_IN_AN_HOUR;
    case WaitTimeUnit.MINUTES:
      return timeValue * SECONDS_IN_A_MINUTE;
    default:
      assertNever(timeUnit);
  }
}

export function getTimeValue({
  seconds,
  timeUnit,
}: {
  seconds: number;
  timeUnit: WaitTimeUnit;
}): number {
  switch (timeUnit) {
    case WaitTimeUnit.DAYS:
      return Math.floor(seconds / SECONDS_IN_A_DAY);
    case WaitTimeUnit.HOURS:
      return Math.floor(seconds / SECONDS_IN_AN_HOUR);
    case WaitTimeUnit.MINUTES:
      return Math.floor(seconds / SECONDS_IN_A_MINUTE);
    default:
      assertNever(timeUnit);
  }
}

export enum WaitTimeUnit {
  DAYS = "Days",
  HOURS = "Hours",
  MINUTES = "Minutes",
}
/** @deprecated Use the new zod flow step types instead */
export interface WaitStep extends BaseStep {
  type: StepType.WAIT;
  numDays: number;
  numSeconds?: number;
  timeUnit: WaitTimeUnit;
  nextId: string;
}
/** @deprecated Use the new zod flow step types instead */
export interface WaitForEventStep extends BaseStep {
  type: StepType.WAIT_FOR_EVENT;
  eventType: SupportedWaitForEventType;
  nextId: string;
}

export enum ConditionDataSource {
  TriggerData = "trigger-data",
  ExistingSegment = "existing-segment",
  InlineSegment = "inline-segment",
  OfferResponse = "offer-response",
}

export const conditionDataSourceToFriendly: Record<
  ConditionDataSource,
  string
> = {
  [ConditionDataSource.TriggerData]: "Trigger-specific condition",
  [ConditionDataSource.ExistingSegment]: "Existing segment membership",
  [ConditionDataSource.InlineSegment]: "Custom segment conditions",
  [ConditionDataSource.OfferResponse]: "Action taken on offer",
};

const ACTIVITY_TYPE_MAP: Record<CustomerActivityType, string> = {
  [CustomerActivityType.ORDER_PLACED_SHOPIFY]: "placed an order",
  [CustomerActivityType.ORDER_PLACED_COMMENTSOLD]: "placed an order",
  [CustomerActivityType.VIEWED_PRODUCT_SHOPIFY]: "viewed a product",
  [CustomerActivityType.VIEWED_PRODUCT_COMMENTSOLD]: "viewed a product",
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_SHOPIFY]: "added to cart",
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_COMMENTSOLD]: "added to cart",
  [CustomerActivityType.CHECKOUT_STARTED_SHOPIFY]: "started checkout",
  [CustomerActivityType.CHECKOUT_STARTED_COMMENTSOLD]: "started checkout",
  [CustomerActivityType.OPENED_EMAIL]: "opened an email",
  [CustomerActivityType.CLICKED_EMAIL]: "clicked an email",
  [CustomerActivityType.RECEIVED_EMAIL]: "received an email",
  [CustomerActivityType.RECEIVED_TEXT]: "received a text",
  [CustomerActivityType.CLICKED_TEXT]: "clicked a text",
  [CustomerActivityType.ACTIVE_ON_SITE_SHOPIFY]: "were active on site",
  [CustomerActivityType.ACTIVE_ON_SITE_COMMENTSOLD]: "were active on site",
  [CustomerActivityType.COLLECTION_VIEWED_SHOPIFY]: "viewed a collection",
  [CustomerActivityType.COLLECTION_VIEWED_COMMENTSOLD]: "viewed a collection",
  [CustomerActivityType.RETURN_PROCESSED]: "processed a return",
  [CustomerActivityType.SUBSCRIBED_TO_BACK_IN_STOCK]:
    "subscribed to back-in-stock notifications",
  [CustomerActivityType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION]:
    "triggered a back-in-stock notification",
};

const CHARACTERISTIC_TYPE_MAP: Record<CustomerCharacteristicType, string> = {
  [CustomerCharacteristicType.SUBSCRIBED_TO_EMAIL]: "are subscribed to email",
  [CustomerCharacteristicType.CAN_RECEIVE_EMAIL_MARKETING]:
    "can receive email marketing",
  [CustomerCharacteristicType.SUBSCRIBED_TO_SMS]: "are subscribed to SMS",
  [CustomerCharacteristicType.CREATED_TIME]: "account creation", // This will be handled specially
  [CustomerCharacteristicType.CUSTOMER_TAGS]: "have tags",
  [CustomerCharacteristicType.STATIC_SEGMENT_MEMBERSHIP]: "are in segment",
  [CustomerCharacteristicType.RETURN_RATE_VALUE]: "have return rate value",
  [CustomerCharacteristicType.RETURN_RATE_COUNT]: "have return rate count",
  [CustomerCharacteristicType.BIRTHDAY]: "birthday date", // This will be handled specially
  [CustomerCharacteristicType.CUSTOM_FIELDS]: "have custom field",
  [CustomerCharacteristicType.STATE_PROVINCE]: "are in state/province",
  [CustomerCharacteristicType.COUNTRY]: "are in country",
  [CustomerCharacteristicType.CITY]: "are in city",
  [CustomerCharacteristicType.PROXIMITY_TO_CITY]: "location proximity",
  [CustomerCharacteristicType.PHONE_NUMBER_AREA_CODE]:
    "area code of phone number",
  [CustomerCharacteristicType.ENROLLED_IN_LOYALTY_LION]:
    "are enrolled in LoyaltyLion loyalty",
  [CustomerCharacteristicType.EMAIL_ADDRESS]: "have email address",
  [CustomerCharacteristicType.CUSTOMER_NAME]: "have name",
};

// Generate descriptive summary for inline segment conditions
export function getInlineSegmentSummary(
  inlineSegment: SegmentConditionBlock,
): string {
  const firstCondition = inlineSegment?.conditions?.[0];

  // Early return for invalid data
  if (!firstCondition?.type) {
    return "Custom segment conditions";
  }

  // Get condition text based on type
  const conditionText =
    firstCondition.type === SegmentConditionType.CUSTOMER_ACTIVITY
      ? getActivitySummary(firstCondition)
      : firstCondition.type === SegmentConditionType.CUSTOMER_ATTRIBUTE
        ? getCharacteristicSummary(firstCondition)
        : null;

  if (!conditionText) {
    return "Custom segment conditions";
  }

  // Build summary with mode and additional conditions count
  const blockMode =
    (inlineSegment.mode || ConjunctionMode.AND) === ConjunctionMode.AND
      ? "All"
      : "Any";
  const additionalCount = inlineSegment.conditions.length - 1;
  const moreText = additionalCount > 0 ? ` +${additionalCount} more` : "";

  return `Customer fits ${blockMode.toLowerCase()} of: ${conditionText}${moreText}`;
}

function getActivitySummary(activity: CustomerActivityCondition): string {
  if (!activity.activityType || !activity.count || !activity.timeframe) {
    return "performed activity";
  }
  const activityTypeText = getActivityTypeText(activity.activityType);
  const countText = getCountText(activity.count);
  const timeframeText = getTimeframeText(activity.timeframe);
  return `${activityTypeText} ${countText} ${timeframeText}`;
}

function getActivityTypeText(activityType: CustomerActivityType): string {
  return ACTIVITY_TYPE_MAP[activityType] || "performed activity";
}

function getCountText(count: ActivityCount): string {
  switch (count.type) {
    case ActivityCountType.AT_LEAST_ONCE:
      return "at least once";
    case ActivityCountType.ZERO_TIMES:
      return "zero times";
    case ActivityCountType.N_TIMES:
      return `exactly ${count.n} time${count.n === 1 ? "" : "s"}`;
    case ActivityCountType.NOT_N_TIMES:
      return `not ${count.n} time${count.n === 1 ? "" : "s"}`;
    case ActivityCountType.AT_LEAST_N:
      return `at least ${count.n} time${count.n === 1 ? "" : "s"}`;
    case ActivityCountType.GREATER_THAN_N:
      return `more than ${count.n} time${count.n === 1 ? "" : "s"}`;
    case ActivityCountType.LESS_THAN_N:
      return `less than ${count.n} time${count.n === 1 ? "" : "s"}`;
    case ActivityCountType.AT_MOST_N:
      return `at most ${count.n} time${count.n === 1 ? "" : "s"}`;
    default:
      return "";
  }
}

function getTimeframeText(timeframe: FullDateTimeframeCondition): string {
  switch (timeframe.type) {
    case TimeframeConditionType.BEFORE_NOW_RELATIVE:
      return `in the last ${timeframe.value} ${timeframe.units}${timeframe.value === 1 ? "" : "s"}`;
    case TimeframeConditionType.ALL_TIME:
      return "over all time";
    case TimeframeConditionType.TODAY:
      return "today";
    case TimeframeConditionType.BEFORE_RELATIVE:
      return `at least ${timeframe.value} ${timeframe.units}${timeframe.value === 1 ? "" : "s"} ago`;
    case TimeframeConditionType.AFTER:
      return `after ${formatDateOnly(timeframe.date)}`;
    case TimeframeConditionType.BEFORE:
      return `before ${formatDateOnly(timeframe.date)}`;
    case TimeframeConditionType.AUTOMATION_START:
      return "since this automation started";
    case TimeframeConditionType.ON:
      return `on ${formatDateOnly(timeframe.date)}`;
    case TimeframeConditionType.BETWEEN_RELATIVE:
      return `between the last ${timeframe.start} and ${timeframe.end} ${timeframe.units}`;
    case TimeframeConditionType.BETWEEN_DATES:
      if (!timeframe.range || timeframe.range.length < 2) {
        return "between dates";
      }
      return `between ${formatDateOnly(timeframe.range[0])} and ${formatDateOnly(timeframe.range[1])}`;
    default:
      return "";
  }
}

function getCharacteristicSummary(
  characteristic: CustomerAttributeCondition,
): string {
  const dimension = characteristic.whereCondition?.dimension;
  if (!dimension) {
    return "have characteristic";
  }

  // Handle special cases with custom formatters
  switch (dimension) {
    case CustomerCharacteristicType.PROXIMITY_TO_CITY:
      return getProximityText(characteristic.whereCondition.comparison);

    case CustomerCharacteristicType.BIRTHDAY:
    case CustomerCharacteristicType.CREATED_TIME:
      return getDateText(dimension, characteristic.whereCondition.comparison);

    case CustomerCharacteristicType.RETURN_RATE_VALUE:
    case CustomerCharacteristicType.RETURN_RATE_COUNT: {
      const characteristicText = getCharacteristicText(dimension);
      const operatorText = characteristic.whereCondition.comparison
        ? getOperatorText(characteristic.whereCondition.comparison)
        : "";

      // Add percent symbol for numeric comparisons
      const suffix =
        operatorText &&
        characteristic.whereCondition.comparison?.type === "numeric"
          ? "%"
          : "";
      return `${characteristicText} ${operatorText}${suffix}`.trim();
    }

    default: {
      // Standard characteristic handling
      const characteristicText = getCharacteristicText(dimension);
      const operatorText = characteristic.whereCondition.comparison
        ? getOperatorText(characteristic.whereCondition.comparison)
        : "";
      return `${characteristicText} ${operatorText}`.trim();
    }
  }
}

function getCharacteristicText(dimension: CustomerCharacteristicType): string {
  return CHARACTERISTIC_TYPE_MAP[dimension] || "have characteristic";
}

// Helper function to generate proximity text for city conditions
function getProximityText(comparison: any): string {
  const city = comparison?.value;
  if (!comparison?.type || comparison.type !== "proximity" || !city) {
    return "are near city";
  }

  const { radius, units = "miles" } = comparison.options || {};
  const isWithin = comparison.operator === "WITHIN";

  if (radius) {
    return `are ${isWithin ? "within" : "outside"} ${radius} ${units} of ${city}`;
  }
  return `are ${isWithin ? "near" : "not near"} ${city}`;
}

// Helper function to generate date text for birthday and created time conditions
function getDateText(
  dimension: CustomerCharacteristicType,
  comparison: any,
): string {
  const dimensionText =
    dimension === CustomerCharacteristicType.BIRTHDAY
      ? "birthday"
      : "account creation";

  if (!comparison) {
    return `have ${dimensionText}`;
  }

  // Handle timeframe-based comparisons (the main case for created time dropdowns)
  if (
    comparison.type ||
    comparison.value !== undefined ||
    comparison.units ||
    comparison.date ||
    comparison.range
  ) {
    const timeframeText = getTimeframeText(comparison);
    if (timeframeText) {
      return `have ${dimensionText} ${timeframeText}`;
    }
  }

  // Fallback for direct date comparisons
  if (comparison.date) {
    const operator = comparison.operator || "on";
    const formattedDate = formatDateOnly(comparison.date);
    switch (operator.toLowerCase()) {
      case "before":
        return `have ${dimensionText} before ${formattedDate}`;
      case "after":
        return `have ${dimensionText} after ${formattedDate}`;
      case "on":
        return `have ${dimensionText} on ${formattedDate}`;
      default:
        return `have ${dimensionText} ${operator} ${formattedDate}`;
    }
  }

  return `have ${dimensionText}`;
}

// Helper function to format dates as MM-DD-YYYY only
function formatDateOnly(dateInput: string | Date): string {
  try {
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) {
      return typeof dateInput === "string" ? dateInput : "Invalid Date";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${month}-${day}-${year}`;
  } catch {
    return typeof dateInput === "string" ? dateInput : "Invalid Date";
  }
}

function getOperatorText(comparison: any): string {
  switch (comparison.type) {
    case "boolean":
      return comparison.value ? "is true" : "is false";
    case "token":
      switch (comparison.operator) {
        case "ANY":
          return comparison.values.length === 1
            ? `containing "${comparison.values[0]}"`
            : `containing any of: ${comparison.values.slice(0, 2).join(", ")}${comparison.values.length > 2 ? "..." : ""}`;
        case "NONE":
          return comparison.values.length === 1
            ? `not containing "${comparison.values[0]}"`
            : `not containing: ${comparison.values.slice(0, 2).join(", ")}${comparison.values.length > 2 ? "..." : ""}`;
        default:
          return "";
      }
    case "numeric": {
      const value = comparison.value;
      switch (comparison.operator) {
        case "eq":
          return `equals ${value}`;
        case "gt":
          return `greater than ${value}`;
        case "lt":
          return `less than ${value}`;
        case "gte":
          return `at least ${value}`;
        case "lte":
          return `at most ${value}`;
        case "neq":
          return `not equal to ${value}`;
        default:
          return "";
      }
    }
    case "list":
      switch (comparison.operator) {
        case "any":
          return `containing any of: ${comparison.values.slice(0, 2).join(", ")}${comparison.values.length > 2 ? "..." : ""}`;
        case "none":
          return `not containing any of: ${comparison.values.slice(0, 2).join(", ")}${comparison.values.length > 2 ? "..." : ""}`;
        case "all":
          return `containing all of: ${comparison.values.slice(0, 2).join(", ")}${comparison.values.length > 2 ? "..." : ""}`;
        default:
          return "";
      }
    case "proximity": {
      const radius = comparison.options?.radius;
      const units = comparison.options?.units;
      const city = comparison.value;
      if (!radius || !units || !city) {
        return "";
      }
      switch (comparison.operator) {
        case "WITHIN":
          return `within ${radius} ${units} of ${city}`;
        case "OUTSIDE":
          return `outside ${radius} ${units} of ${city}`;
        default:
          return "";
      }
    }
    case "date":
    case "date-annual":
      // Handle date-based comparisons by using the timeframe text
      if (!comparison.type) {
        return "";
      }
      return getTimeframeText(comparison);
    default:
      return "";
  }
}

/** @deprecated Use the new zod flow step types instead */
export interface ConditionTriggerDataExpression {
  dataSource: ConditionDataSource.TriggerData;
  schemaBooleanExpression: SchemaBooleanExpression;
}

/** @deprecated Use the new zod flow step types instead */
export interface ConditionExistingSegmentExpression {
  dataSource: ConditionDataSource.ExistingSegment;
  segmentIds: string[];
}

/** @deprecated Use the new zod flow step types instead */
export interface ConditionInlineSegmentExpression {
  dataSource: ConditionDataSource.InlineSegment;
  inlineSegment: SegmentConditionBlock;
}

/** @deprecated Use the new zod flow step types instead */
export interface ConditionOfferResponseExpression {
  dataSource: ConditionDataSource.OfferResponse;
}

/** @deprecated Use the new zod flow step types instead */
export type ConditionExpression =
  | ConditionTriggerDataExpression
  | ConditionExistingSegmentExpression
  | ConditionInlineSegmentExpression
  | ConditionOfferResponseExpression;

export enum UpsellConditionSource {
  PRODUCT = "product",
  COLLECTION = "collection",
}

/** @deprecated Use the new zod flow step types instead */
//TODO: Write a migration script to make these required, and enforce them in the builder
//This should be a discriminated union
export interface UpsellConditionExpression {
  source: UpsellConditionSource;
  products?: { id: string; name: string }[] | undefined;
  collection?: string | undefined;
}

/** @deprecated Use the new zod flow step types instead */
export interface ConditionStep extends BaseStep {
  type: StepType.CONDITION;
  expression: ConditionExpression;
  nextTrueId: string;
  nextFalseId: string;
}

/** @deprecated Use the new zod flow step types instead */
export interface UpsellConditionStep extends BaseStep {
  //TODO: Write a migration script to make these required, and enforce them in the builder
  type: StepType.UPSELL_CONDITION;
  isDefault?: boolean;
  expression?: UpsellConditionExpression;
  nextTrueId?: string;
  nextFalseId?: string;
  name?: string;
}

/** @deprecated Use the new zod flow step types instead */
export interface DoNothingStep extends BaseStep {
  type: StepType.DO_NOTHING;
}

/** @deprecated Use the new zod flow step types instead */
export interface UpsellStep extends BaseStep {
  type: StepType.UPSELL;
  name: string;
  upsellId: string;
  nextId: string;
}

/** @deprecated Use the new zod flow step types instead */
export interface DownsellStep extends BaseStep {
  type: StepType.DOWNSELL;
  name: string;
  downsellId: string;
  nextId: string;
}

/** @deprecated Use the new zod flow step types instead */
export type OmsConditionStep =
  | OmsOrderTagConditionStep
  | OmsSelectedShippingConditionStep
  | OmsSkuConditionStep
  | OmsProductNameConditionStep
  | OmsVariantTitleConditionStep
  | OmsProductTypeConditionStep
  | OmsTotalValueConditionStep
  | OmsTaxesPaidConditionStep
  | OmsPackageWeightOzConditionStep
  | OmsItemQuantityConditionStep
  | OmsDestinationProvinceConditionStep
  | OmsDestinationCountryConditionStep
  | OmsDeliveryMethodConditionStep
  | OmsStreetAddressConditionStep
  | OmsStreetAddressListInputConditionStep
  | OmsSalesChannelConditionStep
  | OmsProductTagConditionStep
  | OmsFulfillmentLocationConditionStep
  | OmsOriginStoreConditionStep
  | OmsOrderNotesConditionStep
  | OmsFraudRiskConditionStep
  | OmsAvailableInventoryConditionStep
  | OmsMarketplaceConditionStep
  | OmsShipmentTagConditionStep
  | OmsCustomString1ConditionStep
  | OmsCustomString2ConditionStep
  | OmsCustomString3ConditionStep
  | OmsCustomNumber1ConditionStep
  | OmsCustomNumber2ConditionStep
  | OmsCustomNumber3ConditionStep
  | OmsCustomDate1ConditionStep
  | OmsCustomDate2ConditionStep
  | OmsCustomDate3ConditionStep
  | OmsCustomAttributeString1ConditionStep
  | OmsCustomAttributeString2ConditionStep
  | OmsCustomAttributeString3ConditionStep
  | OmsCustomAttributeNumber1ConditionStep
  | OmsCustomAttributeNumber2ConditionStep
  | OmsCustomAttributeNumber3ConditionStep
  | OmsCustomAttributeDate1ConditionStep
  | OmsCustomAttributeDate2ConditionStep
  | OmsCustomAttributeDate3ConditionStep
  | OmsCustomerNameConditionStep
  | OmsCustomerOrderCountConditionStep
  | OmsCustomerTagConditionStep
  | OmsItemAmountConditionStep
  | OmsUspsZoneConditionStep
  | OmsVendorConditionStep
  | OmsLineItemCustomAttributeConditionStep
  | OmsAddressVerifiedConditionStep;

/** @deprecated Use the new zod flow step types instead */
export type OmsActionStep =
  | SetShippingServiceActionStep
  | SetParcelActionStep
  | SetShippingServiceCheapestActionStep
  | SetShippingServiceFastestActionStep
  | SetShippingServiceCheapestWithinDeliveryDaysActionStep
  | SetPackingSlipTemplateActionStep
  | AddTagActionStep
  | SetShippingOptionsActionStep
  | PlaceHoldActionStep
  | PlaceHoldUntilActionStep
  | SetWeightActionStep
  | RoundWeightActionStep
  | SetAssignedUserActionStep
  | SetBuyReturnLabelActionStep
  | SetDutiesPayerActionStep
  | SetTaxIdentifiersActionStep
  | SetReturnAddressActionStep
  | AssignFulfillmentServiceActionStep
  | SetOriginLocationActionStep
  | SetThirdPartyBillingAccountActionStep
  | SetDeliveryMethodActionStep
  | SetOverrideUseOriginalDestinationAddressActionStep
  | GeneratePersonalizedSlipActionStep
  | SetInternalNoteActionStep
  | SplitIntoParcelsActionStep;

/** @deprecated Use the new zod flow step types instead */
export type OmsFlowStep =
  | OmsActionStep
  | OmsConditionStep
  | TriggerStep
  | DoNothingStep;

export { StepType } from "./common";
